import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';
import React from 'react';

import HelpIcon from '../../../assets/help-circle.svg';
import {CREATE_FLOWS} from '../../../constants';
import {
  DARK_TEXT_COLOR,
  GREEN_BUTTON,
  GREY_90,
  GREY_BORDER_DEFAULT,
  WHITE_100,
} from '../../../theme';
import {styles} from './CreateDocumentModal.styles';

// Co-located styles for this component
const localStyles = {
  label: {
    color: DARK_TEXT_COLOR,
    fontSize: '0.875rem',
    fontWeight: '500',
    letterSpacing: '-0.14px',
    lineHeight: '130%',
  },
  switch: {
    '& .MuiSwitch-switchBase': {
      '&.Mui-checked': {
        '& + .MuiSwitch-track': {
          backgroundColor: GREEN_BUTTON,
          opacity: 1,
        },
        color: GREEN_BUTTON,
        transform: 'translateX(12px)',
      },
      padding: '2px',
    },
    '& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb': {
      backgroundColor: WHITE_100,
    },
    '& .MuiSwitch-thumb': {
      backgroundColor: GREY_90,
      height: 16,
      width: 16,
    },
    '& .MuiSwitch-track': {
      backgroundColor: GREY_BORDER_DEFAULT,
      borderRadius: 100,
      opacity: 1,
    },
    height: 20,
    padding: 0,
    width: 32,
  },
};

export const AutoLinkingConfig = ({
  affordableHousingEnabled,
  autoLinkingEnabled,
  autoLinkingServiceCode,
  createFlow,
  setAffordableHousingEnabled,
  setAutoLinkingServiceCode,
}) => {
  // Only show for PDF rollforward when LaunchDarkly flag is enabled
  if (createFlow !== CREATE_FLOWS.pdf || !autoLinkingEnabled) {
    return null;
  }

  return (
    <Stack direction="column" spacing="0.5rem">
      <Stack
        alignItems="center"
        direction="row"
        justifyContent="space-between"
        width="100%"
      >
        <Stack alignItems="center" direction="row" spacing="0.5rem">
          <Typography sx={localStyles.label}>
            Enable auto-linking for supported AH engagements.
          </Typography>
          <Tooltip
            enterDelay={300}
            placement="bottom-start"
            title={
              <Typography fontSize={12} sx={{p: '5px'}}>
                Auto-linking is available for Affordable Housing engagements
                (service codes 2000, 2030, 2035). When using the RP-1011.2A HUD
                or RP-1011.2 CR Unregulated Excel templates, Inscope
                automatically inserts table placeholders to prepare PDFs for
                linking.
              </Typography>
            }
          >
            <img
              alt="Info"
              src={HelpIcon}
              style={{cursor: 'pointer', height: 16, width: 16}}
            />
          </Tooltip>
        </Stack>
        <Switch
          checked={affordableHousingEnabled}
          onChange={(e) => {
            setAffordableHousingEnabled(e.target.checked);
            if (!e.target.checked) {
              setAutoLinkingServiceCode('');
            }
          }}
          sx={localStyles.switch}
        />
      </Stack>
      {affordableHousingEnabled && (
        <Stack direction="column" pt={1} spacing="0.5rem">
          <Typography style={styles.modal.label}>Service code *</Typography>
          <FormControl fullWidth>
            <Select
              displayEmpty
              onChange={(e) => setAutoLinkingServiceCode(e.target.value)}
              sx={styles.modal.select}
              value={autoLinkingServiceCode}
            >
              <MenuItem disabled value="">
                Select a service code
              </MenuItem>
              <MenuItem value="2000">2000</MenuItem>
              <MenuItem value="2030">2030</MenuItem>
              <MenuItem value="2035">2035</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      )}
    </Stack>
  );
};

AutoLinkingConfig.propTypes = {
  affordableHousingEnabled: PropTypes.bool.isRequired,
  autoLinkingEnabled: PropTypes.bool.isRequired,
  autoLinkingServiceCode: PropTypes.string.isRequired,
  createFlow: PropTypes.string,
  setAffordableHousingEnabled: PropTypes.func.isRequired,
  setAutoLinkingServiceCode: PropTypes.func.isRequired,
};
